import { useEffect, useState } from "react";
import { ProductsViewModel } from "../../viewmodels/products/ProductsViewModel";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import * as actions from "../../store/actions/index";
import {
  convertStockStatusToPill,
  withRouter,
} from "../../shared/utility";

// Template Components
import DataManagementTemplate from "../../components/templates/DataManagementTemplate";
import MobileCardsTemplate from "../../components/templates/MobileCardsTemplate";

// Other Components
import SlideOver from "../../components/slideOver/SlideOver";
import EmptyModal from "../../components/modals/EmptyModal";
import Pills from "../../components/controls/Pills";

function Products(props) {
  const { logout } = props;
  const navigate = useNavigate();
  const {
    emptyModal,
    setEmptyModelOpen,
    getProducts,
    loading,
    products,
    setSlideOver,
    slideOver,
    filterComponents,
    resetFilters,
    showFilters,
    setShowFilters,
    filters,
    getPrimaryCategoriesForDropdown,
    getSubCategoriesForDropdownByPrimary,
    resetSubCategoryFilter,
  } = ProductsViewModel(logout);

  var currentPage = 1;
  useEffect(() => {
    getProducts(null, currentPage, filters);
    getPrimaryCategoriesForDropdown();
  }, []);

  useEffect(() => {
    resetSubCategoryFilter();
    if (filters.primaryCategory.value > 0) {
      getSubCategoriesForDropdownByPrimary(filters.primaryCategory.value);
    }
  }, [filters.primaryCategory]);

  const [first, setFirst] = useState(1);
  const [rows, setRows] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");

  // Search handler
  const handleSearch = (term) => {
    setSearchTerm(term);
    getProducts(term || null, currentPage, filters);
  };

  // Pagination handler
  const handlePageChange = (event) => {
    currentPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getProducts(searchTerm || null, currentPage, filters);
  };

  // Row click handler
  const handleRowClick = (product) => {
    navigate(`/products/${product.id}`);
  };

  // Button configurations
  const headerButtons = [
    {
      label: "Create Product",
      onClick: () => navigate("/products/create"),
      className: "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500"
    }
  ];

  // Filter configuration
  const filterConfig = {
    dropdown: true,
    components: filterComponents,
    onReset: () => {
      resetFilters();
    },
    onApply: () => {
      getProducts(searchTerm || null, currentPage, filters);
    }
  };

  // Column configurations for DataTable
  const columns = [
    {
      field: "name",
      header: "Name",
      style: { width: '20%' },
      body: (rowData) => (
        <div>
          <span className="font-semibold apollo-subheading text-gray-900">
            {rowData.name}
          </span>
          <br />
          <span className="text-sm apollo-body text-gray-500">
            {rowData.plu}
          </span>
        </div>
      )
    },
    {
      field: "description",
      header: "Description",
      style: { width: '15%' },
      body: (rowData) => (
        <span className="apollo-body">{rowData.description}</span>
      )
    },
    {
      field: "category",
      header: "Category",
      style: { width: '15%' },
      body: (rowData) => (
        <span className="apollo-body">
          {rowData.primary_category_name}
          {rowData.sub_category_name && (
            <span> / {rowData.sub_category_name}</span>
          )}
        </span>
      )
    },
    {
      field: "stock",
      header: "Inventory",
      style: { width: '10%' },
      body: (rowData) => (
        <span className="apollo-body text-end">{rowData.stock}</span>
      )
    },
    {
      field: "low_stock_threshold",
      header: "Reorder Point",
      style: { width: '10%' },
      body: (rowData) => (
        <span className="apollo-body text-end">{rowData.low_stock_threshold}</span>
      )
    },
    {
      field: "taxable",
      header: "Taxable",
      style: { width: '10%' },
      body: (rowData) => (
        <span className="apollo-body">
          {rowData.taxable === 1 ? "Yes" : "No"}
        </span>
      )
    },
    {
      field: "unit_price",
      header: "Price",
      style: { width: '10%' },
      body: (rowData) => (
        <span className="apollo-body text-end">{rowData.unit_price}</span>
      )
    },
    {
      field: "stock_status",
      header: "Stock Status",
      style: { width: '10%' },
      body: (rowData) => (
        <Pills
          content={convertStockStatusToPill(
            rowData.stock,
            rowData.low_stock_threshold
          )}
        />
      )
    },
    {
      header: "Actions",
      style: { width: '10%' },
      body: (rowData) => (
        <Link
          className="inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          to={`/products/${rowData.id}`}
        >
          View
        </Link>
      )
    }
  ];

  // Mobile cards component
  const ProductsMobileCards = ({ data }) => (
    <MobileCardsTemplate
      data={data}
      onCardClick={(product) => navigate(`/products/${product.id}`)}
      onEdit={(product) => navigate(`/products/${product.id}`)}
      editRoute="/products"
      emptyMessage="No products found"
      cardContent={(product) => (
        <div className="flex flex-col space-y-3">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-h6 apollo-heading text-primary-600 mb-1">
                {product.name}
              </h3>
              <p className="text-body-2 apollo-body text-gray-500">
                {product.plu}
              </p>
            </div>
            <div className="flex flex-col items-end space-y-1">
              <Pills
                content={convertStockStatusToPill(
                  product.stock,
                  product.low_stock_threshold
                )}
              />
              {product.taxable === 1 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Taxable
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          {product.description && (
            <div>
              <p className="text-body-2 apollo-body text-gray-600">
                {product.description}
              </p>
            </div>
          )}

          {/* Details */}
          <div className="space-y-2">
            <div className="flex justify-between text-body-2 apollo-body">
              <span>Category:</span>
              <span>
                {product.primary_category_name}
                {product.sub_category_name && ` / ${product.sub_category_name}`}
              </span>
            </div>
            <div className="flex justify-between text-body-2 apollo-body">
              <span>Inventory:</span>
              <span>{product.stock}</span>
            </div>
            <div className="flex justify-between text-body-2 apollo-body">
              <span>Reorder Point:</span>
              <span>{product.low_stock_threshold}</span>
            </div>
            <div className="flex justify-between text-body-2 apollo-subheading text-primary-700 border-t pt-2">
              <span>Price:</span>
              <span>{product.unit_price}</span>
            </div>
          </div>
        </div>
      )}
      actionButtons={(product) => (
        <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
          <Link to={`/products/${product.id}`} className="flex-1">
            <button
              className="w-full inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              View
            </button>
          </Link>
        </div>
      )}
    />
  );

  return (
    <div>
      <DataManagementTemplate
        title="Products"
        description="Manage your product catalog"
        headerButtons={headerButtons}
        filterConfig={filterConfig}
        columns={columns}
        data={products.data || []}
        loading={loading}
        pagination={{
          first: first,
          rows: rows,
          total: products.total || 0,
          template: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
          currentPageReportTemplate: "Showing {first} to {last} of {totalRecords} products"
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        searchPlaceholder="Search by product name"
        MobileCardsComponent={ProductsMobileCards}
        emptyMessage="No products found"
      />
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Products)
);
